import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

const EARTH_RADIUS = 6371000;
const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;
const MAPBOX_SDK_WAIT_TIMEOUT = 5000;
const MAPBOX_SDK_WAIT_INTERVAL = 50;

const GENERATED_BOUNDS_DEFAULT_DISTANCE = 500; // meters
// Distances for generated bounding boxes for different Mapbox place types
const PLACE_TYPE_BOUNDS_DISTANCES = {
  address: 500,
  country: 2000,
  region: 2000,
  postcode: 2000,
  district: 2000,
  place: 2000,
  locality: 2000,
  neighborhood: 2000,
  poi: 2000,
  'poi.landmark': 2000,
};

const isDev = process.env.NODE_ENV === 'development';

const getMapboxSdk = () => (typeof window !== 'undefined' ? window.mapboxSdk : null);

const waitForMapboxSdk = () =>
  new Promise(resolve => {
    if (typeof window === 'undefined') {
      resolve(null);
      return;
    }

    const sdk = getMapboxSdk();
    if (sdk) {
      resolve(sdk);
      return;
    }

    const startedAt = Date.now();
    const checkSdk = () => {
      const sdkMaybe = getMapboxSdk();
      if (sdkMaybe) {
        resolve(sdkMaybe);
      } else if (Date.now() - startedAt >= MAPBOX_SDK_WAIT_TIMEOUT) {
        resolve(null);
      } else {
        window.setTimeout(checkSdk, MAPBOX_SDK_WAIT_INTERVAL);
      }
    };

    window.setTimeout(checkSdk, MAPBOX_SDK_WAIT_INTERVAL);
  });

const fallbackLocationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  const latDelta = (distance / EARTH_RADIUS) * RAD_TO_DEG;
  const lngDelta = (distance / (EARTH_RADIUS * Math.cos(latlng.lat * DEG_TO_RAD))) * RAD_TO_DEG;

  return new SDKLatLngBounds(
    new SDKLatLng(latlng.lat + latDelta, latlng.lng + lngDelta),
    new SDKLatLng(latlng.lat - latDelta, latlng.lng - lngDelta)
  );
};

const locationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  if (typeof window === 'undefined' || !window.mapboxgl?.LngLat) {
    return fallbackLocationBounds(latlng, distance);
  }

  const bounds = new window.mapboxgl.LngLat(latlng.lng, latlng.lat).toBounds(distance);
  return new SDKLatLngBounds(
    new SDKLatLng(bounds.getNorth(), bounds.getEast()),
    new SDKLatLng(bounds.getSouth(), bounds.getWest())
  );
};

const placeOrigin = prediction => {
  if (prediction && Array.isArray(prediction.center) && prediction.center.length === 2) {
    // Coordinates in Mapbox features are represented as [longitude, latitude].
    return new SDKLatLng(prediction.center[1], prediction.center[0]);
  }
  return null;
};

const placeBounds = prediction => {
  if (prediction) {
    if (Array.isArray(prediction.bbox) && prediction.bbox.length === 4) {
      // Bounds in Mapbox features are represented as [minX, minY, maxX, maxY]
      return new SDKLatLngBounds(
        new SDKLatLng(prediction.bbox[3], prediction.bbox[2]),
        new SDKLatLng(prediction.bbox[1], prediction.bbox[0])
      );
    } else {
      // If bounds are not available, generate them around the origin

      // Resolve bounds distance based on place type
      const placeType = Array.isArray(prediction.place_type) && prediction.place_type[0];

      const distance =
        (placeType && PLACE_TYPE_BOUNDS_DISTANCES[placeType]) || GENERATED_BOUNDS_DEFAULT_DISTANCE;

      return locationBounds(placeOrigin(prediction), distance);
    }
  }
  return null;
};

export const GeocoderAttribution = () => null;

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Mapbox Geocoding API.
 */
class GeocoderMapbox {
  constructor(mapsConfig = {}) {
    this.mapsConfig = mapsConfig;
    this._client = null;
    this._missingClientWarningShown = false;
  }

  getAccessToken() {
    return (
      this.mapsConfig?.mapboxAccessToken ||
      process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
      (typeof window !== 'undefined' ? window.mapboxgl?.accessToken : null)
    );
  }

  warnMissingClient(hasSdk, hasAccessToken) {
    if (isDev && !this._missingClientWarningShown) {
      console.warn(
        'Mapbox geocoder is not ready. ' +
          `mapboxSdk loaded: ${hasSdk ? 'yes' : 'no'}, ` +
          `access token configured: ${hasAccessToken ? 'yes' : 'no'}.`
      );
      this._missingClientWarningShown = true;
    }
  }

  async getClient() {
    const mapboxSdk = await waitForMapboxSdk();
    const accessToken = this.getAccessToken();
    const hasSdk = !!mapboxSdk;
    const hasAccessToken = !!accessToken;

    if (!hasSdk || !hasAccessToken) {
      this.warnMissingClient(hasSdk, hasAccessToken);
      return null;
    }

    if (!this._client) {
      this._client = mapboxSdk({
        accessToken,
      });
    }
    return this._client;
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {String} search query for place names
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  getPlacePredictions(search, countryLimit, locale) {
    const limitCountriesMaybe = countryLimit ? { countries: countryLimit } : {};

    return this.getClient().then(client => {
      if (!client) {
        return {
          search,
          predictions: [],
          unavailable: true,
        };
      }

      return client.geocoding
        .forwardGeocode({
          query: search,
          limit: 5,
          ...limitCountriesMaybe,
          language: [locale],
        })
        .send()
        .then(response => {
          return {
            search,
            predictions: response?.body?.features || [],
          };
        });
    });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    return prediction.id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Mapbox geocoding API
    return prediction.place_name;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {Object} prediction selected prediction object
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction, currentLocationBoundsDistance) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        return {
          address: '',
          origin: latlng,
          bounds: locationBounds(latlng, currentLocationBoundsDistance),
        };
      });
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    return Promise.resolve({
      address: this.getPredictionAddress(prediction),
      origin: placeOrigin(prediction),
      bounds: placeBounds(prediction),
    });
  }
}

export default GeocoderMapbox;
